import {
  Outlet,
  Router,
  RouterProvider,
  createRootRoute,
  createRoute,
  redirect,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/auth";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { PortalLayout } from "@/features/portal-shell";
import { HomePage } from "@/features/home/HomePage";
import { LoginPage } from "@/features/auth/LoginPage";
import { RegisterPage } from "@/features/auth/RegisterPage";
import { GuideListPage } from "@/features/guides/GuideListPage";
import { GuideDetailPage } from "@/features/guides/GuideDetailPage";
import { RankPage } from "@/features/rank/RankPage";
import { AboutPage } from "@/features/about/AboutPage";
import { NewGuidePage } from "@/features/portal/NewGuidePage";
import { DashboardPage } from "@/features/admin/DashboardPage";
import { GuidesManagePage } from "@/features/admin/GuidesManagePage";
import { TemplatesPage } from "@/features/admin/TemplatesPage";
import { EvaluationsPage } from "@/features/admin/EvaluationsPage";
import { UsersPage } from "@/features/admin/UsersPage";
import { RolesPage } from "@/features/admin/RolesPage";
import { SwipersPage } from "@/features/admin/SwipersPage";
import { GuideFieldsPage } from "@/features/admin/GuideFieldsPage";
import { TasksPage } from "@/features/admin/TasksPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

const rootRoute = createRootRoute({
  component: () => (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  ),
});

function requireLogin() {
  if (!useAuthStore.getState().loggedIn) {
    throw redirect({ to: "/inx/login" });
  }
}
function requireAdmin() {
  // 管理台对所有登录用户开放(申请人也有"指南管理/任务中心/新建指南"自助功能);
  // 菜单按角色过滤(AdminLayout),数据面由后端 permissions 强制鉴权。
  // 不做前端角色白名单——迁移用户的角色码多样(admin/expert/applicant/reviewer/无绑定),
  // 白名单会把合法用户重定向回首页(验收踩过)。
  requireLogin();
}

// ===== 落地首页(任务 P0.8):全屏落地形态,不套 PortalLayout,直接挂根路由 =====
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

// ===== 门户布局(两段式顶栏,任务 0.2)=====
// 2026-09 结构复刻:门户应用规范路径统一挂 /inx 前缀,对齐旧站 /inx/* 部署结构;
// 旧无前缀路径由下方 legacyPortalRedirect 系列路由全部重定向过来。
const portalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "portal-layout",
  beforeLoad: ({ location }) => {
    // 旧站 hash 路由书签承接:旧门户是 hash 路由,书签形如 /inx/#/search、
    // /inx/#/guide/123、/inx/#/detail?id=N。hash 永不到达服务器,SPA 只收到
    // /inx/(或 /inx,命中下方 inxIndexRoute),在这里把 hash 内路径改写为 /inx/*
    // 规范地址,replace 不留历史记录。注意 ParsedLocation.hash 不含前导 "#"
    // (#/search → "/search")。放在门户布局路由上:所有门户页通用,且先于子路由执行。
    const { hash } = location;
    if (!hash.startsWith("/")) return;
    // #/(旧门户默认页)→ 落地首页
    if (hash === "/") throw redirect({ to: "/", replace: true });
    // 旧详情 hash #/detail?id=N(旧证书二维码/外链此格式)→ 新详情页
    const detail = hash.match(/^\/detail\?.*\bid=(\d+)/);
    if (detail) throw redirect({ href: `/inx/guide/${detail[1]}`, replace: true });
    throw redirect({ href: `/inx${hash}`, replace: true });
  },
  component: PortalLayout,
});

// 旧门户应用根 /inx(无 hash):回落地首页;带 #/ 的 hash 书签由上面 PortalLayout
// beforeLoad shim 先行承接(父级 beforeLoad 先于本路由执行,不会走到这里)
const inxIndexRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx",
  beforeLoad: () => {
    throw redirect({ to: "/", replace: true });
  },
});

// /inx/search /inx/regInx /inx/myInx 三态共用 GuideListPage(P0.3 已重写为门户形态,内部按路径判定模式)
const searchRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/search",
  component: GuideListPage,
});
const regInxRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/regInx",
  component: GuideListPage,
});
const myInxRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/myInx",
  beforeLoad: requireLogin,
  component: GuideListPage,
});
const rankRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/rank",
  component: RankPage,
});
const aboutRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/about",
  component: AboutPage,
});
const guideDetailRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/inx/guide/$guideId",
  component: GuideDetailPage,
});

// ===== 登录/注册:独立全屏页(AuthLayout,对齐旧站 login 独立页,不套门户顶栏)=====
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: AuthLayout,
});
const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/inx/login",
  component: LoginPage,
});
const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/inx/register",
  component: RegisterPage,
});
// 找回密码页已下线(无 SMTP 通道,已决策;后端端点保留待用)
// 智能助手 /admin/ai-reviews /admin/ai-ops 页面已下线(AI 功能页待重做)
// 新建指南收进管理台(不再挂门户):/admin/guides/new
const newGuideRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/guides/new",
  component: NewGuidePage,
});
// 旧门户侧新建地址 → 管理台
const legacyInxNewGuideRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inx/portal/new-guide",
  beforeLoad: () => {
    throw redirect({ to: "/admin/guides/new", replace: true });
  },
});
const legacyPortalNewGuideRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal/new-guide",
  beforeLoad: () => {
    throw redirect({ to: "/admin/guides/new", replace: true });
  },
});

// ===== 旧无前缀路径兼容重定向(2026-09 结构复刻)=====
// 生产已切流,旧外链不能断:v2 前期门户页挂在前缀外(/search 等),现规范地址迁到
// /inx 前缀;旧地址一律客户端重定向(replace,不留历史)。href 整体前缀透传,
// 路径参数与查询串自动保留(如 /guide/123?x=1 → /inx/guide/123?x=1)。
function legacyPortalRedirect(path: string) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: ({ location }) => {
      throw redirect({ href: `/inx${location.href}`, replace: true });
    },
  });
}
const legacySearchRedirect = legacyPortalRedirect("/search");
const legacyRegInxRedirect = legacyPortalRedirect("/regInx");
const legacyMyInxRedirect = legacyPortalRedirect("/myInx");
const legacyRankRedirect = legacyPortalRedirect("/rank");
const legacyAboutRedirect = legacyPortalRedirect("/about");
const legacyGuideDetailRedirect = legacyPortalRedirect("/guide/$guideId");
const legacyLoginRedirect = legacyPortalRedirect("/login");
const legacyRegisterRedirect = legacyPortalRedirect("/register");

// ===== 更早期路径兼容重定向(任务 0.10;目标同步指向 /inx/* 规范地址)=====
const guidesCompatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guides",
  beforeLoad: () => {
    throw redirect({ to: "/inx/search", replace: true });
  },
});
const guideDetailCompatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guides/$guideId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/inx/guide/$guideId",
      params: { guideId: params.guideId },
      replace: true,
    });
  },
});
const myGuidesCompatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal/my-guides",
  beforeLoad: () => {
    throw redirect({ to: "/inx/myInx", replace: true });
  },
});

// ===== 管理后台布局(自定义壳:侧边栏 + Tabbar 多标签 + Toolbar,复刻 yqbf-next)=====
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin-layout",
  beforeLoad: requireAdmin,
  component: AdminLayout,
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin",
  component: DashboardPage,
});
const adminDashboardAliasRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/dashboard",
  component: DashboardPage,
});
const adminTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/templates",
  component: TemplatesPage,
});
const adminEvaluationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/evaluations",
  component: EvaluationsPage,
});
const adminGuidesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/guides",
  component: GuidesManagePage,
});
const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/users",
  component: UsersPage,
});
const adminRolesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/roles",
  component: RolesPage,
});
const adminSwipersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/swipers",
  component: SwipersPage,
});
const adminGuideFieldsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/guide-fields",
  component: GuideFieldsPage,
});
const adminTasksRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/tasks",
  component: TasksPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  portalLayoutRoute.addChildren([
    searchRoute,
    regInxRoute,
    myInxRoute,
    rankRoute,
    aboutRoute,
    guideDetailRoute,
    inxIndexRoute,
  ]),
  authLayoutRoute.addChildren([loginRoute, registerRoute]),
  legacySearchRedirect,
  legacyRegInxRedirect,
  legacyMyInxRedirect,
  legacyRankRedirect,
  legacyAboutRedirect,
  legacyGuideDetailRedirect,
  legacyLoginRedirect,
  legacyRegisterRedirect,
  legacyInxNewGuideRedirect,
  legacyPortalNewGuideRedirect,
  guidesCompatRoute,
  guideDetailCompatRoute,
  myGuidesCompatRoute,
  adminLayoutRoute.addChildren([
    adminDashboardRoute,
    adminDashboardAliasRoute,
    adminGuidesRoute,
    newGuideRoute,
    adminTemplatesRoute,
    adminEvaluationsRoute,
    adminTasksRoute,
    adminUsersRoute,
    adminRolesRoute,
    adminSwipersRoute,
    adminGuideFieldsRoute,
  ]),
]);

export const router = new Router({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
